import { StrictMode } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import App from './App';
import './styles.css';

type AnalyticsWindow = Window & {
  dataLayer?: IArguments[];
  gtag?: (...args: unknown[]) => void;
  cisoOperatorConversionReceipt?: string;
};

const analyticsWindow = window as AnalyticsWindow;
const pagePath = window.location.pathname;
const isConfirmationPage = pagePath.startsWith('/thank-you');
const urlParameters = new URLSearchParams(window.location.search);
const conversionReceipt = urlParameters.get('receipt') || '';

// Preserve a validated receipt in memory for the confirmation component, then
// remove it before Google Analytics receives a page location. Lead IDs must not
// become analytics URL parameters or remain in browser history.
if (isConfirmationPage && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(conversionReceipt)) {
  analyticsWindow.cisoOperatorConversionReceipt = conversionReceipt;
  window.history.replaceState(null, '', pagePath);
}

analyticsWindow.dataLayer ||= [];
analyticsWindow.gtag ||= function gtag(..._args: unknown[]) {
  analyticsWindow.dataLayer?.push(arguments);
};
analyticsWindow.gtag('js', new Date());
analyticsWindow.gtag('config', 'G-7S7V3FEYPC', isConfirmationPage ? { send_page_view: false } : {});

// gtag.js is the page's largest transfer; ordinary pages load it after the load
// event to keep it off the critical path. Validated confirmation pages load it
// immediately so a short visit does not lose the accepted-lead measurement.
function loadAnalytics() {
  if (['localhost', '127.0.0.1', '[::1]'].includes(location.hostname)) return;
  // The secure-contact page serves people who may be under attack; it never
  // loads gtag.js, so no visit to it reaches Google Analytics.
  if (pagePath === '/secure-contact') return;
  if (document.getElementById('gtag-js')) return;
  const script = document.createElement('script');
  script.id = 'gtag-js';
  script.async = true;
  script.src = 'https://www.googletagmanager.com/gtag/js?id=G-7S7V3FEYPC';
  document.head.appendChild(script);
}
const hasPaidClickSignal = Boolean(
  urlParameters.get('gclid')
  || urlParameters.get('gbraid')
  || urlParameters.get('wbraid')
  || ['cpc', 'ppc', 'paid', 'paid-search'].includes((urlParameters.get('utm_medium') || '').toLowerCase()),
);

if (isConfirmationPage || hasPaidClickSignal) {
  loadAnalytics();
} else if (document.readyState === 'complete') {
  setTimeout(loadAnalytics, 1500);
} else {
  window.addEventListener('load', () => setTimeout(loadAnalytics, 1500), { once: true });
}

const rootElement = document.getElementById('root')!;
const app = (
  <StrictMode>
    <App />
  </StrictMode>
);
// Prerendered pages hydrate; client-only routes (thank-you pages) mount fresh.
if (rootElement.firstElementChild) {
  hydrateRoot(rootElement, app);
} else {
  createRoot(rootElement).render(app);
}
